import { useClearRolodexEntriesCachedByIdOnScopeChange } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

type PipelineConfigTags = Record<string, string | string[]>

type PipelineConfigStatusEntry = {
  entry: {
    id: string
  }
}

interface PipelineConfigContextType {
  isPreviewMode: boolean
  setIsPreviewMode: React.Dispatch<React.SetStateAction<boolean>>
  activePipelineConfigId: string | undefined
  previewPipelineConfigId: string | undefined
  selectedPipelineConfigId: string | undefined
  pipelineConfigCacheScope: string | undefined
  hasPreviewConfig: boolean
  isPipelineConfigScopeLoading: boolean
  getPipelineConfigScopedTags: (tags: PipelineConfigTags) => PipelineConfigTags
}

const PipelineConfigContext = createContext<
  PipelineConfigContextType | undefined
>(undefined)

export const PipelineConfigProvider: React.FC<{
  children: React.ReactNode
  siteID: string
}> = ({ children, siteID }) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const { data: activePipelineConfigEntries } =
    useGetRolodexEntriesByLoadOptions<PipelineConfigStatusEntry[]>({
      type: ['pipeline-config'],
      tags: { site: siteID },
      filters: [{ field: 'data', key: 'status', op: '=', value: 'active' }],
      limit: 1,
    })
  const { data: previewPipelineConfigEntries } =
    useGetRolodexEntriesByLoadOptions<PipelineConfigStatusEntry[]>({
      type: ['pipeline-config'],
      tags: { site: siteID },
      filters: [{ field: 'data', key: 'status', op: '=', value: 'preview' }],
      limit: 1,
    })

  const activePipelineConfigId = activePipelineConfigEntries?.[0]?.entry.id
  const previewPipelineConfigId = previewPipelineConfigEntries?.[0]?.entry.id
  const hasLoadedPreviewPipelineConfig =
    previewPipelineConfigEntries !== undefined
  const hasPreviewConfig = previewPipelineConfigId !== undefined
  const selectedPipelineConfigId = isPreviewMode
    ? previewPipelineConfigId
    : activePipelineConfigId
  const hasLoadedSelectedPipelineConfig = isPreviewMode
    ? hasLoadedPreviewPipelineConfig
    : activePipelineConfigEntries !== undefined
  const isPipelineConfigScopeLoading = !hasLoadedSelectedPipelineConfig
  const pipelineConfigCacheScope = isPipelineConfigScopeLoading
    ? undefined
    : `${siteID}:${selectedPipelineConfigId ?? 'legacy'}`

  useClearRolodexEntriesCachedByIdOnScopeChange(pipelineConfigCacheScope)

  useEffect(() => {
    if (hasLoadedPreviewPipelineConfig && !hasPreviewConfig && isPreviewMode) {
      setIsPreviewMode(false)
    }
  }, [hasLoadedPreviewPipelineConfig, hasPreviewConfig, isPreviewMode])

  const getPipelineConfigScopedTags = useCallback(
    (tags: PipelineConfigTags): PipelineConfigTags => {
      const tagsWithoutConfig = { ...tags }
      delete tagsWithoutConfig['pipeline-config']

      if (!selectedPipelineConfigId) {
        return tagsWithoutConfig
      }

      return {
        ...tagsWithoutConfig,
        'pipeline-config': selectedPipelineConfigId,
      }
    },
    [selectedPipelineConfigId]
  )

  const contextValue = useMemo(
    () => ({
      isPreviewMode,
      setIsPreviewMode,
      activePipelineConfigId,
      previewPipelineConfigId,
      selectedPipelineConfigId,
      pipelineConfigCacheScope,
      hasPreviewConfig,
      isPipelineConfigScopeLoading,
      getPipelineConfigScopedTags,
    }),
    [
      activePipelineConfigId,
      getPipelineConfigScopedTags,
      hasPreviewConfig,
      isPipelineConfigScopeLoading,
      isPreviewMode,
      pipelineConfigCacheScope,
      previewPipelineConfigId,
      selectedPipelineConfigId,
    ]
  )

  return (
    <PipelineConfigContext.Provider value={contextValue}>
      {children}
    </PipelineConfigContext.Provider>
  )
}

export const usePipelineConfig = (): PipelineConfigContextType => {
  const context = useContext(PipelineConfigContext)
  if (context === undefined) {
    throw new Error(
      'usePipelineConfig must be used within a PipelineConfigProvider'
    )
  }
  return context
}
